import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Star, Navigation2, Route, Mountain, MapPin } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from '@/contexts/LocationContext';
import { getDistanceKm, formatDistance } from '@/lib/distance';
import { OpenStatusBadge } from '@/components/OpenStatusBadge';
import { OfficialGuachincheBadge, KidsBadge } from '@/components/PlaceBadges';
import { Badge } from '@/components/ui/badge';

const L = {
  es: { details: 'Ver detalles', directions: 'Cómo llegar', trail: 'Ver sendero', toStart: 'hasta el inicio', fromYou: 'desde ti' },
  en: { details: 'View details', directions: 'Directions', trail: 'View trail', toStart: 'to trailhead', fromYou: 'from you' },
  de: { details: 'Details ansehen', directions: 'Route', trail: 'Wanderweg ansehen', toStart: 'bis zum Start', fromYou: 'von dir' },
};
const DIFF = { 'Fácil': { es: 'Fácil', en: 'Easy', de: 'Leicht' }, 'Moderada': { es: 'Moderada', en: 'Moderate', de: 'Mittel' }, 'Difícil': { es: 'Difícil', en: 'Hard', de: 'Schwer' } };

const useSwipeDown = (onClose) => {
  const startY = useRef(null);
  return {
    onTouchStart: (e) => { startY.current = e.touches[0].clientY; },
    onTouchEnd: (e) => { if (startY.current !== null && e.changedTouches[0].clientY - startY.current > 60) onClose(); startY.current = null; },
  };
};

const Sheet = ({ onClose, color, children, testId }) => {
  const swipe = useSwipeDown(onClose);
  return (
    <div className="absolute left-0 right-0 z-20 px-3 flex justify-center" style={{ bottom: 'calc(6rem + env(safe-area-inset-bottom, 0px))' }} data-testid={testId}>
      <div {...swipe} className="relative w-full max-w-md bg-white rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.22)] overflow-hidden animate-in slide-in-from-bottom-6 fade-in duration-300" style={{ borderTop: `4px solid ${color}` }}>
        <div className="flex justify-center pt-1.5 -mb-1"><span className="w-10 h-1 rounded-full bg-gray-200" /></div>
        <button onClick={onClose} className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/90 shadow flex items-center justify-center text-gray-600 hover:text-gray-900" aria-label="close" data-testid="map-card-close"><X size={15} /></button>
        {children}
      </div>
    </div>
  );
};

export const PlaceSheet = ({ place, typology, categoryName, onClose }) => {
  const { getLocalizedField, language } = useLanguage();
  const { location, isRealLocation } = useLocation();
  const navigate = useNavigate();
  const t = L[language] || L.es;
  const name = getLocalizedField(place, 'name');
  const dist = isRealLocation && location?.lat ? getDistanceKm(location.lat, location.lng, place.latitude, place.longitude) : null;
  const Icon = typology.icon;
  const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${place.latitude},${place.longitude}${place.google_place_id ? `&destination_place_id=${place.google_place_id}` : ''}`;

  return (
    <Sheet onClose={onClose} color={typology.color} testId="map-place-card">
      <div className="flex gap-3 p-3">
        <img src={place.images?.[0] || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400'} alt={name} className="w-24 h-24 rounded-xl object-cover shrink-0" />
        <div className="min-w-0 flex-1 pr-6">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-white" style={{ backgroundColor: typology.color }} data-testid="map-card-typology">
              <Icon size={11} /> {typology.label[language] || typology.label.es}
            </span>
            {categoryName && <span className="text-[11px] text-gray-500">{categoryName}</span>}
          </div>
          <h3 className="font-semibold text-gray-900 text-base leading-tight line-clamp-2" data-testid="map-card-name">{name}</h3>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-600 flex-wrap">
            {place.google_rating && <span className="flex items-center gap-0.5"><Star size={12} className="text-amber-500" fill="currentColor" />{place.google_rating}</span>}
            {dist !== null && <span className="flex items-center gap-0.5" data-testid="map-card-distance"><Navigation2 size={11} className="text-[#38B2AC]" />{formatDistance(dist)} {t.fromYou}</span>}
            <OpenStatusBadge openingHours={place.opening_hours} placeId={place.id} />
          </div>
          {(place.official_guachinche || place.kids_friendly) && (
            <div className="flex gap-1 mt-1.5">
              {place.official_guachinche && <OfficialGuachincheBadge testId={`map-card-official-${place.id}`} />}
              {place.kids_friendly && <KidsBadge testId={`map-card-kids-${place.id}`} />}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2 px-3 pb-3">
        <button onClick={() => navigate(`/place/${place.id}`)} className="flex-1 py-2 rounded-full text-sm font-semibold text-white active:scale-[0.98] transition-transform" style={{ backgroundColor: '#006D77' }} data-testid="map-card-details-btn">{t.details}</button>
        <a href={gmaps} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border border-gray-200 text-gray-800 hover:bg-gray-50" data-testid="map-card-directions-btn"><MapPin size={14} /> {t.directions}</a>
      </div>
    </Sheet>
  );
};

export const TrailSheet = ({ trail, typology, onClose }) => {
  const { language } = useLanguage();
  const { location, isRealLocation } = useLocation();
  const navigate = useNavigate();
  const t = L[language] || L.es;
  const dist = isRealLocation && location?.lat ? getDistanceKm(location.lat, location.lng, trail.start_latitude, trail.start_longitude) : null;
  const diffCls = trail.difficulty === 'Fácil' ? 'bg-green-50 text-green-700 border-green-200' : trail.difficulty === 'Moderada' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-red-50 text-red-700 border-red-200';

  return (
    <Sheet onClose={onClose} color={typology.color} testId="map-trail-card">
      <div className="flex gap-3 p-3">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${typology.color}1F` }}>
          <Mountain size={26} style={{ color: typology.color }} />
        </div>
        <div className="min-w-0 flex-1 pr-6">
          <div className="flex items-center gap-1.5 mb-1">
            {trail.matricula && <span className="text-[11px] font-mono bg-gray-100 px-1.5 py-0.5 rounded">{trail.matricula}</span>}
            <Badge variant="outline" className={`text-[11px] ${diffCls}`}>{DIFF[trail.difficulty]?.[language] || trail.difficulty}</Badge>
          </div>
          <h3 className="font-semibold text-gray-900 text-base leading-tight line-clamp-2" data-testid="map-card-name">{trail.name}</h3>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-600 flex-wrap">
            <span className="flex items-center gap-0.5"><Route size={12} />{(trail.distance_meters / 1000).toFixed(1)} km</span>
            {trail.duration_hours && <span>~{trail.duration_hours}h</span>}
            {trail.elevation_gain != null && <span>+{Math.round(trail.elevation_gain)} m</span>}
            {dist !== null && <span className="flex items-center gap-0.5" data-testid="map-card-distance"><Navigation2 size={11} className="text-[#38B2AC]" />{formatDistance(dist)} {t.toStart}</span>}
          </div>
        </div>
      </div>
      <div className="px-3 pb-3">
        <button onClick={() => navigate(`/trails/${trail.id}`)} className="w-full py-2 rounded-full text-sm font-semibold text-white active:scale-[0.98] transition-transform" style={{ backgroundColor: typology.color }} data-testid="map-card-trail-btn">{t.trail}</button>
      </div>
    </Sheet>
  );
};
